import { type AnyRouteMatch, type useLocation } from "@tanstack/react-router";
import type i18next from "i18next";
import { type IconName } from "icons";
import { assert, featureFlagName } from "utils";

import {
  type BillingProductFeatureKey,
  billingProductFeatureCopy,
} from "src/helpers/billingProductFeatures";

import { type BreadcrumbData, type SuccessAuthenticate } from "./types";

export interface BaseStaticContextWithoutParams {
  i18n: typeof i18next;
  session: SuccessAuthenticate | null;
  location: ReturnType<typeof useLocation>;
  pathname: string;
}

export interface BaseStaticContext extends BaseStaticContextWithoutParams {
  params: Record<string, string>;
}

export interface SidebarRouteMatch {
  pathname: string;
}

export interface SidebarSubItemConfig {
  id: string;
  text: string;
  href: string;
  activeWhen?: (match: SidebarRouteMatch) => boolean;
}

export interface SidebarItemConfig {
  id: string;
  iconName: IconName;
  text: string;
  href: string;
  locked?: boolean;
  activeWhen?: (match: SidebarRouteMatch) => boolean;
  items?: SidebarSubItemConfig[];
}

export interface SidebarGroupConfig {
  id: string;
  label?: string;
  items: SidebarItemConfig[];
}

interface SidebarLogoHeaderConfig {
  type: "logo";
}

interface SidebarLinkHeaderConfig {
  type: "link";
  iconName?: IconName;
  text?: string;
  href?: string;
  tooltip?: string;
}

export type SidebarHeaderConfig =
  | SidebarLogoHeaderConfig
  | SidebarLinkHeaderConfig;

export interface SidebarTabContentConfig {
  type: "aiChats";
}

export interface SidebarTabConfig {
  value: string;
  iconName: IconName;
  text: string;
  activeWhen?: (match: SidebarRouteMatch) => boolean;
  content?: SidebarTabContentConfig;
}

export interface SidebarTabsConfig {
  defaultValue: string;
  items: SidebarTabConfig[];
}

export interface SidebarConfig {
  header?: SidebarHeaderConfig;
  tabs?: SidebarTabsConfig;
  groups: SidebarGroupConfig[];
  showAccountMenu?: boolean;
  showCommunicationActions?: boolean;
}

export interface LayoutSettings {
  authNavigationTitle?: string;
  requiresCoachSubscription?: boolean;
  showAuthSidebar?: boolean;
  useAuthLayout?: boolean;
  sidebar?: SidebarConfig | null;
}

interface GetLayoutSettingsOptions {
  matches: AnyRouteMatch[];
  ctx: BaseStaticContextWithoutParams;
}

export function getLayoutSettings(
  options: GetLayoutSettingsOptions,
): LayoutSettings {
  return options.matches.reduce<LayoutSettings>((settings, match) => {
    if (!isMatchWithLayoutSettings(match)) {
      return settings;
    }

    return {
      ...settings,
      ...match.staticData.getLayoutSettings({
        ...options.ctx,
        params: match.params,
      }),
    };
  }, getDefaultLayoutSettings(options.ctx));
}

export function getDefaultLayoutSettings(
  ctx: BaseStaticContextWithoutParams,
): LayoutSettings {
  return {
    sidebar: getDefaultSidebarConfig(ctx),
  };
}

export function getDefaultSidebarConfig(
  ctx: BaseStaticContextWithoutParams,
): SidebarConfig | null {
  if (!ctx.session) {
    return null;
  }

  if (ctx.session.coach) {
    return getCoachSidebarConfig(ctx);
  }

  if (ctx.session.athlete) {
    return getAthleteSidebarConfig(ctx);
  }

  return null;
}

export function getOrganizationSidebarConfig(
  ctx: BaseStaticContextWithoutParams,
  organizationSlug: string,
): SidebarConfig {
  const organizationPath = `/orgs/${organizationSlug}`;

  return {
    groups: [
      {
        id: "organization-main",
        items: [
          {
            id: "organization-overview",
            iconName: "tabler-users-group",
            text: ctx.i18n.t("organizations.overview"),
            href: organizationPath,
            activeWhen: (match) => {
              return match.pathname === organizationPath;
            },
          },
          {
            id: "organization-athletes",
            iconName: "tabler-users",
            text: ctx.i18n.t("organizations.athletes"),
            href: `${organizationPath}/athletes`,
            activeWhen: (match) => {
              return match.pathname.startsWith(`${organizationPath}/athletes`);
            },
          },
          {
            id: "organization-programs",
            iconName: "tabler-folder",
            text: ctx.i18n.t("sidebar.programs"),
            href: `${organizationPath}/programs`,
            activeWhen: (match) => {
              return match.pathname.startsWith(`${organizationPath}/programs`);
            },
          },
          {
            id: "organization-workouts",
            iconName: "tabler-timeline",
            text: ctx.i18n.t("sidebar.workouts"),
            href: `${organizationPath}/workouts`,
            activeWhen: (match) => {
              return match.pathname.startsWith(`${organizationPath}/workouts`);
            },
          },
          {
            id: "organization-exercises",
            iconName: "tabler-barbell",
            text: ctx.i18n.t("sidebar.exercises"),
            href: `${organizationPath}/exercises`,
            activeWhen: (match) => {
              return match.pathname.startsWith(`${organizationPath}/exercises`);
            },
          },
          {
            id: "organization-labels",
            iconName: "tabler-tag",
            text: ctx.i18n.t("sidebar.labels"),
            href: `${organizationPath}/labels`,
            activeWhen: (match) => {
              return match.pathname.startsWith(`${organizationPath}/labels`);
            },
          },
        ],
      },
    ],
  };
}

function getPersonalSettingsGroup(
  ctx: BaseStaticContextWithoutParams,
  paths: {
    integrations: string;
    preferences: string;
    profile: string;
  },
): SidebarGroupConfig {
  return {
    id: "settings-personal",
    label: ctx.i18n.t("settingsNavigation.personal"),
    items: [
      {
        id: "settings-profile",
        iconName: "tabler-user-circle",
        text: ctx.i18n.t("settingsNavigation.profile"),
        href: paths.profile,
        activeWhen: (match) => {
          return match.pathname === paths.profile;
        },
      },
      {
        id: "settings-preferences",
        iconName: "tabler-user",
        text: ctx.i18n.t("settingsNavigation.preferences"),
        href: paths.preferences,
        activeWhen: (match) => {
          return match.pathname === paths.preferences;
        },
      },
      {
        id: "settings-integrations",
        iconName: "tabler-plug-connected",
        text: ctx.i18n.t("integrations"),
        href: paths.integrations,
        activeWhen: (match) => {
          return match.pathname.startsWith(paths.integrations);
        },
      },
    ],
  };
}

function getBillingSettingsItem(
  ctx: BaseStaticContextWithoutParams,
  billingPath: string,
): SidebarItemConfig {
  return {
    id: "settings-billing",
    iconName: "tabler-credit-card",
    text: ctx.i18n.t("billing.title"),
    href: billingPath,
    activeWhen: (match) => {
      return match.pathname.startsWith(billingPath);
    },
  };
}

function getMcpSettingsItem(
  ctx: BaseStaticContextWithoutParams,
  mcpPath: string,
): SidebarItemConfig {
  return {
    id: "settings-mcp",
    iconName: "tabler-sparkles",
    text: ctx.i18n.t("settingsNavigation.mcp"),
    href: mcpPath,
    activeWhen: (match) => {
      return match.pathname === mcpPath;
    },
  };
}

export function getOrganizationSettingsSidebarConfig(
  ctx: BaseStaticContextWithoutParams,
  organizationSlug: string,
): SidebarConfig {
  const organizationPath = `/orgs/${organizationSlug}`;
  const settingsPath = `${organizationPath}/settings`;

  return {
    header: {
      type: "link",
      iconName: "tabler-arrow-left",
      text: ctx.i18n.t("settingsNavigation.backToApp"),
      href: organizationPath,
      tooltip: ctx.i18n.t("settingsNavigation.backToApp"),
    },
    showAccountMenu: false,
    showCommunicationActions: false,
    groups: [
      getPersonalSettingsGroup(ctx, {
        integrations: `${settingsPath}/integrations`,
        preferences: `${settingsPath}/preferences`,
        profile: `${settingsPath}/profile`,
      }),
      {
        id: "organization-administration",
        label: ctx.i18n.t("settingsNavigation.administration"),
        items: [
          {
            id: "organization-settings",
            iconName: "tabler-users-group",
            text: ctx.i18n.t("organizations.organization"),
            href: settingsPath,
            activeWhen: (match) => {
              return match.pathname === settingsPath;
            },
          },
          {
            id: "organization-members",
            iconName: "tabler-users",
            text: ctx.i18n.t("organizations.members"),
            href: `${settingsPath}/members`,
            activeWhen: (match) => {
              return match.pathname === `${settingsPath}/members`;
            },
          },
          getBillingSettingsItem(ctx, `${settingsPath}/billing`),
        ],
      },
      {
        id: "settings-ai",
        label: ctx.i18n.t("settingsNavigation.ai"),
        items: [getMcpSettingsItem(ctx, `${settingsPath}/mcp`)],
      },
    ],
  };
}

export function getSettingsSidebarConfig(
  ctx: BaseStaticContextWithoutParams,
): SidebarConfig {
  const groups = [
    getPersonalSettingsGroup(ctx, {
      integrations: "/settings/integrations",
      preferences: "/settings/preferences",
      profile: "/settings",
    }),
  ];

  if (ctx.session?.coach) {
    groups.push({
      id: "settings-ai",
      label: ctx.i18n.t("settingsNavigation.ai"),
      items: [getMcpSettingsItem(ctx, "/settings/mcp")],
    });
  }

  if (ctx.session?.coach) {
    groups.push({
      id: "settings-administration",
      label: ctx.i18n.t("settingsNavigation.administration"),
      items: [getBillingSettingsItem(ctx, "/settings/billing")],
    });
  }

  return {
    header: {
      type: "link",
      iconName: "tabler-arrow-left",
      text: ctx.i18n.t("settingsNavigation.backToApp"),
      href: "/",
      tooltip: ctx.i18n.t("settingsNavigation.backToApp"),
    },
    showAccountMenu: false,
    showCommunicationActions: false,
    groups,
  };
}

function getCoachSidebarConfig(
  ctx: BaseStaticContextWithoutParams,
): SidebarConfig {
  function isFeatureLocked(feature: BillingProductFeatureKey): boolean {
    assert(ctx.session, "Session is required to check feature lock status");
    const entitlement = billingProductFeatureCopy[feature].entitlement;
    return !ctx.session.entitlements[entitlement];
  }

  const groups: SidebarGroupConfig[] = [
    {
      id: "coach-main",
      items: [
        {
          id: "coach-schedule",
          iconName: "tabler-calendar",
          text: ctx.i18n.t("schedule"),
          href: "/",
          activeWhen: (match) => {
            return match.pathname === "/";
          },
        },
        {
          id: "coach-athletes",
          iconName: "tabler-users",
          text: ctx.i18n.t("sidebar.athletes"),
          href: "/athletes",
          activeWhen: (match) => {
            return match.pathname.startsWith("/athletes");
          },
        },
        {
          id: "coach-public-profile",
          iconName: "tabler-user-star",
          text: ctx.i18n.t("publicProfile"),
          href: "/profile/links",
          activeWhen: (match) => {
            return match.pathname.startsWith("/profile");
          },
        },
        {
          id: "coach-products",
          iconName: "tabler-shopping-bag",
          text: ctx.i18n.t("sidebar.products"),
          href: "/products",
          locked: isFeatureLocked("sellProducts"),
          activeWhen: (match) => {
            return match.pathname.startsWith("/products");
          },
        },
        {
          id: "coach-workflows",
          iconName: "tabler-users-group",
          text: ctx.i18n.t("sidebar.workflows"),
          href: "/workflows",
          locked: isFeatureLocked("trainingGroups"),
          activeWhen: (match) => {
            return match.pathname.startsWith("/workflows");
          },
        },
        {
          id: "coach-automations",
          iconName: "tabler-bolt",
          text: ctx.i18n.t("sidebar.automations"),
          href: "/automations",
          activeWhen: (match) => {
            return match.pathname.startsWith("/automations");
          },
        },
      ],
    },
    {
      id: "coach-library",
      items: [
        {
          id: "coach-content-hub",
          iconName: "tabler-books",
          text: ctx.i18n.t("sidebar.contentHub"),
          href: "/content-hub",
          locked: isFeatureLocked("contentHub"),
          activeWhen: (match) => {
            return match.pathname.startsWith("/content-hub");
          },
        },
        {
          id: "coach-forms",
          iconName: "tabler-file-text",
          text: ctx.i18n.t("sidebar.forms"),
          href: "/questionnaires",
          activeWhen: (match) => {
            return match.pathname.startsWith("/questionnaires");
          },
        },
        {
          id: "coach-programs",
          iconName: "tabler-folder",
          text: ctx.i18n.t("sidebar.programs"),
          href: "/library/programs",
          activeWhen: (match) => {
            return match.pathname.startsWith("/library/programs");
          },
        },
        {
          id: "coach-workouts",
          iconName: "tabler-timeline",
          text: ctx.i18n.t("sidebar.workouts"),
          href: "/library/workouts",
          activeWhen: (match) => {
            return match.pathname.startsWith("/library/workouts");
          },
        },
        {
          id: "coach-sections",
          iconName: "tabler-section",
          text: ctx.i18n.t("sidebar.sections"),
          href: "/library/sections",
          activeWhen: (match) => {
            return match.pathname.startsWith("/library/sections");
          },
        },
        {
          id: "coach-exercises",
          iconName: "tabler-barbell",
          text: ctx.i18n.t("sidebar.exercises"),
          href: "/library/exercises",
          activeWhen: (match) => {
            return match.pathname.startsWith("/library/exercises");
          },
        },
        {
          id: "coach-tasks",
          iconName: "tabler-clipboard-check",
          text: ctx.i18n.t("sidebar.tasks"),
          href: "/library/tasks",
          activeWhen: (match) => {
            return match.pathname.startsWith("/library/tasks");
          },
        },
        {
          id: "coach-resources",
          iconName: "tabler-link",
          text: ctx.i18n.t("sidebar.resources"),
          href: "/library/resources",
          activeWhen: (match) => {
            return match.pathname.startsWith("/library/resources");
          },
        },
      ],
    },
    {
      id: "coach-secondary",
      items: [
        {
          id: "coach-labels",
          iconName: "tabler-tag",
          text: ctx.i18n.t("sidebar.labels"),
          href: "/labels",
          activeWhen: (match) => {
            return match.pathname.startsWith("/labels");
          },
        },
      ],
    },
  ];

  if (ctx.session?.user.role === "ADMIN") {
    groups.push(getAdminSidebarGroup(ctx));
  }

  return {
    header: { type: "logo" },
    tabs: getIsAiIntegrationEnabled(ctx)
      ? getCoachSidebarTabsConfig(ctx)
      : undefined,
    groups: groups,
  };
}

function getCoachSidebarTabsConfig(
  ctx: BaseStaticContextWithoutParams,
): SidebarTabsConfig {
  return {
    defaultValue: "browse",
    items: [
      {
        value: "browse",
        iconName: "tabler-layout-grid",
        text: ctx.i18n.t("sidebar.browse"),
      },
      {
        value: "chat",
        iconName: "tabler-message-circle",
        text: ctx.i18n.t("sidebar.chat"),
        activeWhen: (match) => {
          return match.pathname.startsWith("/ai");
        },
        content: { type: "aiChats" },
      },
    ],
  };
}

function getAthleteSidebarConfig(
  ctx: BaseStaticContextWithoutParams,
): SidebarConfig {
  return {
    header: { type: "logo" },
    groups: [
      {
        id: "athlete-main",
        items: [
          {
            id: "athlete-calendar",
            iconName: "tabler-calendar",
            text: ctx.i18n.t("sidebar.calendar"),
            href: "/",
            activeWhen: (match) => {
              return match.pathname === "/";
            },
          },
          {
            id: "athlete-invitations",
            iconName: "tabler-users-group",
            text: ctx.i18n.t("invitations"),
            href: "/invitations",
            activeWhen: (match) => {
              return match.pathname === "/invitations";
            },
          },
          {
            id: "athlete-programs",
            iconName: "tabler-folder",
            text: ctx.i18n.t("sidebar.programs"),
            href: "/programs",
            activeWhen: (match) => {
              return match.pathname === "/programs";
            },
          },
          {
            id: "athlete-forms",
            iconName: "tabler-file-text",
            text: ctx.i18n.t("sidebar.forms"),
            href: "/questionnaire-submissions",
            activeWhen: (match) => {
              return match.pathname === "/questionnaire-submissions";
            },
          },
          {
            id: "athlete-payments",
            iconName: "tabler-credit-card",
            text: ctx.i18n.t("sidebar.payments"),
            href: "/payments",
            activeWhen: (match) => {
              return match.pathname === "/payments";
            },
          },
        ],
      },
    ],
  };
}

function getAdminSidebarGroup(
  ctx: BaseStaticContextWithoutParams,
): SidebarGroupConfig {
  return {
    id: "admin",
    label: ctx.i18n.t("sidebar.admin.label"),
    items: [
      {
        id: "admin-bundles",
        iconName: "tabler-archive",
        text: ctx.i18n.t("sidebar.admin.bundles"),
        href: "/bundles",
        activeWhen: (match) => {
          return match.pathname === "/bundles";
        },
      },
      {
        id: "admin-promo-codes",
        iconName: "tabler-tag",
        text: ctx.i18n.t("sidebar.admin.promoCodes"),
        href: "/admin/invitation-codes",
        activeWhen: (match) => {
          return match.pathname === "/admin/invitation-codes";
        },
      },
      {
        id: "admin-feature-flags",
        iconName: "tabler-tag",
        text: ctx.i18n.t("sidebar.admin.featureFlags"),
        href: "/admin/feature-flags",
        activeWhen: (match) => {
          return match.pathname === "/admin/feature-flags";
        },
      },
      {
        id: "admin-strava-webhook",
        iconName: "tabler-activity",
        text: ctx.i18n.t("sidebar.admin.stravaWebhook"),
        href: "/admin/strava-webhook",
        activeWhen: (match) => {
          return match.pathname === "/admin/strava-webhook";
        },
      },
      {
        id: "admin-metrics",
        iconName: "tabler-chart-line",
        text: ctx.i18n.t("sidebar.admin.metrics"),
        href: "/admin/metrics",
        activeWhen: (match) => {
          return match.pathname === "/admin/metrics";
        },
      },
      {
        id: "admin-products",
        iconName: "tabler-shopping-bag",
        text: ctx.i18n.t("sidebar.admin.products"),
        href: "/admin/products",
        activeWhen: (match) => {
          return match.pathname === "/admin/products";
        },
      },
      {
        id: "admin-billing",
        iconName: "tabler-credit-card",
        text: ctx.i18n.t("sidebar.admin.billing"),
        href: "/admin/billing",
        activeWhen: (match) => {
          return match.pathname.startsWith("/admin/billing");
        },
      },
    ],
  };
}

function getIsAiIntegrationEnabled(ctx: BaseStaticContextWithoutParams) {
  return (
    ctx.session?.user.featureFlags?.includes(featureFlagName.aiIntegration) ===
    true
  );
}

function isMatchWithLayoutSettings(
  match: AnyRouteMatch,
): match is AnyRouteMatch & {
  staticData: {
    getLayoutSettings: (ctx: BaseStaticContext) => LayoutSettings;
  };
} {
  return Boolean(
    match.staticData &&
    typeof match.staticData === "object" &&
    "getLayoutSettings" in match.staticData &&
    typeof match.staticData.getLayoutSettings === "function",
  );
}

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    getLayoutSettings?: (ctx: BaseStaticContext) => LayoutSettings;
    getBreadcrumb?: (ctx: BaseStaticContext) => BreadcrumbData;
    getScrollRestoration?: (ctx: BaseStaticContext) => boolean;
  }

  interface HistoryState {
    globalProgress?: boolean;
  }
}
